import isEmpty from "lodash/isEmpty";
import sortBy from "lodash/sortBy";
import { useEdgeToEdgeLayout } from "PFApp/app_shell_layout.utils";
import { PageFooter } from "PFApp/components/page_footer";
import {
  FORM_ROLES_KEY,
  FORM_SUBTEMPLATES_KEY,
  NO_SUBTEMPLATE_VALUE
} from "PFApp/constants/activity_form/activity_form_field_keys";
import { TemplateKey } from "PFApp/constants/templates";
import { useMultiActivityFormStore } from "PFApp/stores/activity_form/multi_roles_form";
import { useManageRolesStore, useManageRolesStoreActions } from "PFApp/stores/manage_roles";
import { RolesFormKey } from "PFApp/types/activity_form/activity_form_schema";
import { ManageRolesMultiForm } from "PFApp/types/activity_form/manage_roles_form_schema";
import { Accordion } from "PFComponents/accordion";
import { Button } from "PFComponents/button";
import { Stack } from "PFComponents/containers";
import { MovableDivider } from "PFComponents/movable_divider";
import { SimplePagination } from "PFComponents/pagination/simple_pagination";
import { Typography } from "PFComponents/typography";
import { useSubtemplates } from "PFCore/hooks/queries/subtemplates/use_subtemplates";
import { Fragment, MutableRefObject, RefCallback, useCallback, useRef, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import css from "./manage_roles_page.module.scss";
import { ManageRolesAccordionContent } from "./parts/manage_roles_accordion_content";
import { PendingEditsModal } from "./parts/pending_edits_modal";
import { PendingEditsRouterPrompt } from "./parts/pending_edits_router_prompt";
import { usePendingEditsSyncContext } from "./parts/pending_edits_sync_context";
import { Toolbar } from "./parts/toolbar";

const INITIAL_LEFT_WIDTH = 80;
const MIN_LEFT_WIDTH = 20;
const MIN_RIGHT_WIDTH = 10;

type ManageRolesPageContentProps = {
  totalRoles: number;
  totalPages: number;
  fillableVacanciesCount: number;
  isFillAndBookProcessing: boolean;
  onFillAndBook: () => void;
};

export const ManageRolesPageContent = ({
  totalRoles,
  totalPages,
  fillableVacanciesCount,
  isFillAndBookProcessing,
  onFillAndBook
}: ManageRolesPageContentProps) => {
  useEdgeToEdgeLayout();

  const navigate = useNavigate();

  const { t: tTranslation } = useTranslation();
  const { t } = useTranslation("activities", { keyPrefix: "manageRoles" });

  const { data: subtemplates = [] } = useSubtemplates({ templateKey: TemplateKey.Role });

  const currentPage = useManageRolesStore((state) => state.currentPage);
  const { setCurrentPage, hasPendingEdits } = useManageRolesStoreActions();

  const { flushPendingEdits, pauseSync, resumeSync } = usePendingEditsSyncContext();

  const scrollableContainerRef = useMultiActivityFormStore((state) => state.scrollableContainerRef);
  const containerRef = useRef<HTMLDivElement>(null);

  const [containerElement, setContainerElement] = useState<HTMLDivElement | null>(null);

  const [pendingPage, setPendingPage] = useState<number | null>(null);

  const leftPanelRefs = useRef<(HTMLDivElement | null)[]>([]);
  const movableDividerRef = useRef<HTMLDivElement | null>(null);
  const currentWidthRef = useRef<number>(INITIAL_LEFT_WIDTH);

  const getLeftPanelRef = useCallback(
    (index: number): RefCallback<HTMLDivElement> =>
      (node) => {
        leftPanelRefs.current[index] = node;
        if (node) {
          node.style.setProperty("width", `${currentWidthRef.current}%`);
        }
      },
    []
  );

  const handleMovableDividerRef: RefCallback<HTMLDivElement> = useCallback((node) => {
    movableDividerRef.current = node;
    if (node) {
      node.style.setProperty("left", `${currentWidthRef.current}%`);
    }
  }, []);

  const handleContainerRef = useCallback((node: HTMLDivElement | null) => {
    (containerRef as MutableRefObject<HTMLDivElement | null>).current = node;
    setContainerElement(node);
  }, []);

  const handleCancel = () => {
    navigate("/");
  };

  const handlePaginationClick = useCallback(
    (page: number) => {
      if (hasPendingEdits()) {
        pauseSync();
        setPendingPage(page);
      } else {
        setCurrentPage(page);
      }
    },
    [hasPendingEdits, pauseSync, setCurrentPage]
  );

  const handleModalSaveAndContinue = useCallback(async () => {
    const allSaved = await flushPendingEdits();
    if (!allSaved) {
      setPendingPage(null);
      resumeSync();
      return;
    }
    if (pendingPage !== null) {
      setCurrentPage(pendingPage);
    }
    setPendingPage(null);
  }, [flushPendingEdits, pendingPage, resumeSync, setCurrentPage]);

  const handleModalCancel = useCallback(() => {
    setPendingPage(null);
    resumeSync();
  }, [resumeSync]);

  const { control } = useFormContext<ManageRolesMultiForm>();

  const sortedSubtemplateFields = useWatch<ManageRolesMultiForm, typeof FORM_SUBTEMPLATES_KEY>({
    control,
    name: FORM_SUBTEMPLATES_KEY,
    compute: (subtemplates = []) => {
      const withIndex = (subtemplates ?? []).map((item, index) => ({
        subtemplate_key: item.subtemplate_key,
        fieldIndex: index
      }));
      return sortBy(withIndex, [
        (item) => (item.subtemplate_key === NO_SUBTEMPLATE_VALUE ? 1 : 0),
        "subtemplate_key"
      ]);
    }
  }) as { subtemplate_key: string; fieldIndex: number }[];

  const handleRouterPromptOK = useCallback(async () => {
    const allSaved = await flushPendingEdits();
    return allSaved;
  }, [flushPendingEdits]);

  const handleMovableDividerResize = useCallback((leftPanelWidth: number) => {
    if (!movableDividerRef.current) {
      return;
    }
    currentWidthRef.current = leftPanelWidth;
    leftPanelRefs.current.forEach((panel) => {
      panel?.style.setProperty("width", `${leftPanelWidth}%`);
    });
    movableDividerRef.current.style.setProperty("left", `${leftPanelWidth}%`);
  }, []);

  return (
    <Stack gap={null} className={css.root}>
      <PendingEditsRouterPrompt onOK={handleRouterPromptOK} />
      {pendingPage !== null && (
        <PendingEditsModal onSaveAndContinue={handleModalSaveAndContinue} onCancel={handleModalCancel} />
      )}
      <Stack as="header" justifyContent="center" gap={null}>
        <Typography variant="h2" className={css.title}>
          {t("title")}
        </Typography>
        <Toolbar totalRoles={totalRoles} />
      </Stack>
      <Stack ref={scrollableContainerRef} className={css.scrollableContainer}>
        <Stack ref={handleContainerRef} className={css.content} gap="spacing3xl">
          {sortedSubtemplateFields.map(({ fieldIndex, subtemplate_key: subtemplateKey }, index) => {
            const subtemplate = subtemplates.find(
              ({ subtemplate_key }) => subtemplate_key === subtemplateKey
            );

            const rolesFormKey = `${FORM_SUBTEMPLATES_KEY}.${fieldIndex}.${FORM_ROLES_KEY}` as RolesFormKey;

            return (
              <Fragment key={subtemplateKey}>
                <Accordion
                  header={
                    subtemplateKey === NO_SUBTEMPLATE_VALUE
                      ? t("noSubtemplate")
                      : (subtemplate?.subtemplate_title ?? "")
                  }
                  headerVariant="bodyBold"
                  headerFullWidth
                  animated={false}
                  keepMounted
                  iconSet="chevron"
                >
                  <ManageRolesAccordionContent
                    rolesFormKey={rolesFormKey}
                    subtemplate={subtemplate ?? null}
                    leftPanelRef={getLeftPanelRef(index)}
                  />
                </Accordion>
              </Fragment>
            );
          })}
          {!isEmpty(sortedSubtemplateFields) && (
            <MovableDivider
              ref={handleMovableDividerRef}
              qaId="ManageRolesMovableDivider.Handle"
              containerElement={containerElement}
              initialLeftWidth={currentWidthRef.current}
              minLeftWidth={MIN_LEFT_WIDTH}
              minRightWidth={MIN_RIGHT_WIDTH}
              onResize={handleMovableDividerResize}
              unit="percent"
              className={css.movableDivider}
            />
          )}
        </Stack>
      </Stack>
      <PageFooter>
        <Button kind="secondary" text={tTranslation("close")} onClick={handleCancel} />
        {totalPages > 1 && (
          <SimplePagination
            currentPage={currentPage}
            totalPages={totalPages}
            handleClick={handlePaginationClick}
            className={css.pagination}
          />
        )}
        {fillableVacanciesCount > 0 ? (
          <Button
            kind="primary"
            text={t("fillAndBook", { count: fillableVacanciesCount })}
            disabled={isFillAndBookProcessing}
            onClick={onFillAndBook}
            qaId="ManageRolesFooter.FillAndBookButton"
          />
        ) : (
          <div />
        )}
      </PageFooter>
    </Stack>
  );
};
